import { type ChangeEvent, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type PaginationProps = {
  /** Row count; omit or `undefined` is treated as 0. */
  totalRows?: number;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  handleRowsPerPageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** When true, footer stays visible for a single page (server-driven lists). */
  alwaysShowFooter?: boolean;
  /** Override default rows-per-page options; current `rowsPerPage` is included if missing. */
  pageSizeOptions?: readonly number[];
};

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50] as const;

/**
 * Page controls: current page / total, numeric page buttons with ellipses, rows-per-page select.
 * Renders nothing when there is a single page or no rows.
 */
export function Pagination({
  totalRows,
  rowsPerPage,
  currentPage,
  onPageChange,
  handleRowsPerPageChange,
  alwaysShowFooter = false,
  pageSizeOptions,
}: PaginationProps) {
  const { t } = useTranslation();
  const rowsPerPageSelectId = useId();

  const pageCount = useMemo(() => {
    const total = totalRows ?? 0;
    if (rowsPerPage > 0 && total > 0) {
      return Math.ceil(total / rowsPerPage);
    }
    return 1;
  }, [totalRows, rowsPerPage]);

  const resolvedPageSizeOptions = useMemo(() => {
    const base = pageSizeOptions ?? ROWS_PER_PAGE_OPTIONS;
    if (base.includes(rowsPerPage)) {
      return base;
    }
    return [...base, rowsPerPage].sort((a, b) => a - b);
  }, [pageSizeOptions, rowsPerPage]);

  const total = totalRows ?? 0;
  if (total <= 0) {
    return null;
  }

  if (pageCount <= 1 && !alwaysShowFooter) {
    return null;
  }

  const pageNumbers = Array.from({ length: pageCount }, (_, i) => i + 1);
  const showPageButtons = pageCount > 1;

  return (
    <div
      data-testid="widgets-pagination"
      className="flex flex-row w-full justify-between text-xs text-gray-600 dark:text-gray-400 p-2"
    >
      <div className="page-count" data-testid="widgets-pagination-page-count">
        {t('page')} {currentPage} {t('of')} {pageCount}
      </div>
      <div className="pagination " data-testid="widgets-pagination-page-buttons">
        {showPageButtons
          ? pageNumbers.map((page) => {
          const isFirst = page === 1;
          const isLast = page === pageCount;
          const isNearCurrent = Math.abs(currentPage - page) <= 2;

          if (isFirst || isLast || isNearCurrent) {
            return (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                className={`ml-1 px-3 py-2 rounded-full transition-colors duration-200 ${
                  page === currentPage
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}
              >
                {page}
              </button>
            );
          }

          if (
            (page === currentPage - 3 && page >= 2) ||
            (page === currentPage + 3 && page <= pageCount - 1)
          ) {
            return (
              <span key={`ellipsis-${page}`} className="mx-2 text-gray-500 dark:text-gray-400">
                …
              </span>
            );
          }
          return null;
        })
          : null}
      </div>
      <div
        className="rows-per-page rounded-md bg-transparent px-2 py-1"
        data-testid="widgets-pagination-rows-per-page"
      >
        <label htmlFor={rowsPerPageSelectId} className="text-gray-700 dark:text-gray-300">
          {t('pagination.rows-per-page')}:
        </label>
        <select
          id={rowsPerPageSelectId}
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
          className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-1 ml-1 border border-gray-200 dark:border-gray-600 rounded hover:cursor-pointer hover:font-bold transition-colors duration-200"
        >
          {resolvedPageSizeOptions.map((number) => (
            <option key={number} value={number}>
              {number}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
